/**
 * Calculate Offer API Client
 * 
 * Provides function to call the calculate-offer Edge Function for server-side price calculation
 * 
 * SECURITY NOTE:
 * - Uses Supabase anon key (public endpoint)
 * - Optionally uses Supabase Auth token if available (for rate limiting)
 * - Read-only operation (no mutations)
 */

import { supabase } from '../config/supabase';

// Get Supabase configuration from environment
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Types matching Edge Function response
export interface CalculateOfferRequest {
  device_model_key: string;
  storage_key: string;
  answers: Record<string, string>; // question_key -> option_key mapping
}

export interface PriceAdjustment {
  source: string; // e.g., "condition:A"
  type: 'none' | 'fixed' | 'percent' | 'multiplier' | 'special';
  value: number | null;
  delta: number;
}

export interface CalculateOfferResponse {
  status: 'ok';
  device_model_id: string;
  base_price: number;
  adjustments: PriceAdjustment[];
  final_price: number | null;
  is_on_request: boolean;
}

/**
 * Calculate offer price using Edge Function
 * 
 * @param payload - Request payload with device_model_key, storage_key, and answers
 * @returns Promise resolving to CalculateOfferResponse
 * @throws Error with code property for error handling
 */
export async function calculateOffer(
  payload: CalculateOfferRequest
): Promise<CalculateOfferResponse> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error('Supabase configuration missing. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY');
  }

  const url = `${SUPABASE_URL}/functions/v1/calculate-offer`;

  try {
    // Try to get current session for rate limiting, but don't force login
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    // Supabase Edge Functions require Authorization header
    // Use session token if available, otherwise use anon key for anonymous access
    const authToken = accessToken || SUPABASE_ANON_KEY;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${authToken}`,
    };

    console.log('[calculateOffer] Making API request', {
      url,
      hasSession: !!accessToken,
      usingAnonKey: !accessToken,
    });

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      // Parse error from Edge Function response format
      const errorCode = data.code || 'UNKNOWN_ERROR';
      const errorMessage = data.error || `HTTP ${response.status}: ${response.statusText}`;

      const error = new Error(errorMessage) as Error & { code?: string; details?: any };
      error.code = errorCode;
      error.details = data.details;
      throw error;
    }

    // Edge Function returns data directly (jsonSuccess wraps it)
    // Response format: { status: 'ok', device_model_id, base_price, adjustments, final_price, is_on_request }
    return data as CalculateOfferResponse;
  } catch (err) {
    // Re-throw if it's already our Error with code
    if (err instanceof Error && (err as any).code) {
      throw err;
    }

    // Network or other errors
    const error = new Error(
      err instanceof Error ? err.message : 'Failed to calculate offer'
    ) as Error & { code?: string };
    error.code = 'NETWORK_ERROR';
    throw error;
  }
}
